/*
 * QQQ - Low-code Application Framework for Engineers.
 * Copyright (C) 2021-2022.  Kingsrook, LLC
 * 651 N Broad St Ste 205 # 6917 | Middletown DE 19709 | United States
 * contact@kingsrook.com
 * https://github.com/Kingsrook/
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import {AdornmentType} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/AdornmentType";
import {QFieldMetaData} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QFieldMetaData";
import {QFieldType} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QFieldType";
import {QTableMetaData} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QTableMetaData";
import {FieldPossibleValueProps} from "qqq/models/fields/FieldPossibleValueProps";
import ValueUtils from "qqq/utils/qqq/ValueUtils";
import * as Yup from "yup";


export type DisabledFields = { [fieldName: string]: boolean } | string[];

type DynamicFormFieldType = "number" | "datetime-local" | "password" | "time" | "date" | "file" | "checkbox" | "text" | "ace"

//////////////////////////////////////
// type returned by getDynamicField //
//////////////////////////////////////
export interface DynamicFormFieldDefinition
{
   fieldMetaData: QFieldMetaData;
   name: string;
   label: string;
   isRequired: boolean;
   isEditable: boolean;
   type: DynamicFormFieldType;
   displayFormat?: string;
   possibleValueProps?: FieldPossibleValueProps;
   omitFromQDynamicForm?: boolean;
}

////////////////////////////////////////////////////////////////////////////////////////////////
// type returned by getFormData:  associative arrays of field definitions and yup validations //
////////////////////////////////////////////////////////////////////////////////////////////////
export interface FormDataDefinition
{
   dynamicFormFields: Record<string, DynamicFormFieldDefinition>,
   formValidations: Record<string, Yup.Schema>
}

/*******************************************************************************
 ** Meta-data to represent a single field in a table.
 **
 *******************************************************************************/
class DynamicFormUtils
{

   /*******************************************************************************
    **
    *******************************************************************************/
   public static getFormData(qqqFormFields: QFieldMetaData[], disabledFields?: DisabledFields): FormDataDefinition
   {
      const dynamicFormFields: Record<string, DynamicFormFieldDefinition> = {};
      const formValidations: Record<string, Yup.Schema> = {};

      qqqFormFields.forEach((field) =>
      {
         dynamicFormFields[field.name] = this.getDynamicField(field, disabledFields);
         formValidations[field.name] = this.getValidationForField(field, disabledFields);
      });

      return {dynamicFormFields, formValidations};
   }


   /*******************************************************************************
    **
    *******************************************************************************/
   public static getDynamicField(field: QFieldMetaData, disabledFields?: DisabledFields): DynamicFormFieldDefinition
   {
      let fieldType: string;
      switch (field.type.toString())
      {
         case QFieldType.DECIMAL:
         case QFieldType.INTEGER:
            fieldType = "number";
            break;
         case QFieldType.DATE_TIME:
            fieldType = "datetime-local";
            break;
         case QFieldType.PASSWORD:
         case QFieldType.TIME:
         case QFieldType.DATE:
            fieldType = field.type.toString();
            break;
         case QFieldType.BLOB:
            fieldType = "file";
            break;
         case QFieldType.BOOLEAN:
            fieldType = "checkbox";
            break;
         case QFieldType.TEXT:
         case QFieldType.HTML:
         case QFieldType.STRING:
         default:
            fieldType = "text";
      }

      let more: any = {};
      if (field.hasAdornment(AdornmentType.CODE_EDITOR))
      {
         fieldType = "ace";
         const values = field.getAdornment(AdornmentType.CODE_EDITOR).values;
         if (values && values.has("languageMode"))
         {
            more.languageMode = values.get("languageMode");
         }
      }

      ////////////////////////////////////////////////////////////
      // this feels right, but... might be cases where it isn't //
      ////////////////////////////////////////////////////////////
      const effectiveIsEditable = field.isEditable && !this.isFieldDynamicallyDisabled(field.name, disabledFields);
      const effectivelyIsRequired = field.isRequired && effectiveIsEditable;

      let label = field.label ? field.label : field.name;
      label += effectivelyIsRequired ? " *" : "";

      return ({
         fieldMetaData: field,
         name: field.name,
         label: label,
         isRequired: effectivelyIsRequired,
         isEditable: effectiveIsEditable,
         type: fieldType,
         displayFormat: field.displayFormat,
         ////////////////////////////////////////////////////////////
         // todo invalidMsg: "Zipcode is not valid (e.g. 70000).", //
         ////////////////////////////////////////////////////////////
         ...more
      });
   }


   /*******************************************************************************
    **
    *******************************************************************************/
   public static getValidationForField(field: QFieldMetaData, disabledFields?: DisabledFields): Yup.Schema
   {
      const effectiveIsEditable = field.isEditable && !this.isFieldDynamicallyDisabled(field.name, disabledFields);
      const effectivelyIsRequired = field.isRequired && effectiveIsEditable;

      if (effectivelyIsRequired)
      {
         ////////////////////////////////////////////////////////////////////////////////////////////
         // nullable() must come BEFORE required(): in yup 1.x the last call wins, and             //
         // required().nullable() re-allows null - which would let a cleared possible-value or     //
         // removed file pass required validation.  nullable().required() rejects null/""/undefined //
         ////////////////////////////////////////////////////////////////////////////////////////////
         return (Yup.string().nullable().required(`${field.label ?? "This field"} is required.`));
      }
      return (null);
   }


   /*******************************************************************************
    * wrapper around addPossibleValueProps to do it for a single field.
    *******************************************************************************/
   public static addPossibleValuePropsToSingleField(dynamicFormField: DynamicFormFieldDefinition, qField: QFieldMetaData, tableName: string, processName: string, displayValues: Map<string, string>)
   {
      const dynamicFormFields: Record<string, DynamicFormFieldDefinition> = {};
      dynamicFormFields[dynamicFormField.name] = dynamicFormField;
      const qFields = [qField];
      DynamicFormUtils.addPossibleValueProps(dynamicFormFields, qFields, tableName, processName, displayValues);
   }


   /*******************************************************************************
    * update several DynamicFormFieldDefinition's with properties that make them
    * behave like possible value objects, based on attributes in QFieldMetaData.
    *******************************************************************************/
   public static addPossibleValueProps(dynamicFormFields: Record<string, DynamicFormFieldDefinition>, qFields: QFieldMetaData[], tableName: string, processName: string, displayValues: Map<string, string>)
   {
      for (let i = 0; i < qFields.length; i++)
      {
         const field = qFields[i];

         if(!dynamicFormFields[field.name])
         {
            continue;
         }

         /////////////////////////////////////////
         // add props for possible value fields //
         /////////////////////////////////////////
         if (field.possibleValueSourceName || field.inlinePossibleValueSource)
         {
            let props: FieldPossibleValueProps =
               {
                  isPossibleValue: true,
                  fieldName: field.name,
                  initialDisplayValue: null
               }

            if (displayValues)
            {
               props.initialDisplayValue = displayValues.get(field.name);
            }

            if(field.inlinePossibleValueSource)
            {
               //////////////////////////////////////////////////////////////////////
               // handle an inline PVS - which is a list of possible value objects //
               //////////////////////////////////////////////////////////////////////
               props.possibleValues = field.inlinePossibleValueSource;
            }
            else if (tableName)
            {
               props.tableName = tableName;
            }
            else if (processName)
            {
               props.processName = processName;
            }
            else
            {
               props.possibleValueSourceName = field.possibleValueSourceName;
            }

            if(field.possibleValueSourceFilter)
            {
               props.possibleValueSourceFilter = field.possibleValueSourceFilter;
            }

            dynamicFormFields[field.name].possibleValueProps = props;
         }
      }
   }


   /*******************************************************************************
    ** private helper - check the disabled fields object (array or map), and return
    ** true iff fieldName is in it.
    *******************************************************************************/
   private static isFieldDynamicallyDisabled(fieldName: string, disabledFields?: DisabledFields): boolean
   {
      if (!disabledFields)
      {
         return (false);
      }

      if (Array.isArray(disabledFields))
      {
         return (disabledFields.indexOf(fieldName) > -1);
      }
      else
      {
         return (disabledFields[fieldName]);
      }
   }


   /***************************************************************************
    * check if a field has the TO_UPPER_CASE behavior on it.
    ***************************************************************************/
   public static isToUpperCase(fieldMetaData: QFieldMetaData): boolean
   {
      return this.hasBehavior(fieldMetaData, "TO_UPPER_CASE");
   }


   /***************************************************************************
    * check if a field has the TO_LOWER_CASE behavior on it.
    ***************************************************************************/
   public static isToLowerCase(fieldMetaData: QFieldMetaData): boolean
   {
      return this.hasBehavior(fieldMetaData, "TO_LOWER_CASE");
   }


   /***************************************************************************
    * Build the dynamicFormFields + formValidations for a table — wraps the
    * always-paired getFormData() + addPossibleValueProps() call sequence
    * shared by RecordScreen and the row-editor.
    ***************************************************************************/
   public static buildDynamicFormFields(tableMetaData: QTableMetaData, displayValues: Map<string, string>, disabledFields?: DisabledFields, processName?: string): FormDataDefinition
   {
      const fieldsArray = Array.from(tableMetaData.fields.values());
      const {dynamicFormFields, formValidations} = DynamicFormUtils.getFormData(fieldsArray, disabledFields);
      DynamicFormUtils.addPossibleValueProps(dynamicFormFields, fieldsArray, tableMetaData.name, processName ?? null, displayValues);
      return {dynamicFormFields, formValidations};
   }


   /***************************************************************************
    * Get the map of field-name → default-value for a table's fields, for
    * pre-populating a create-mode form.  A falsy default (false, 0, "") is
    * still a default — only null/undefined mean "no default".
    ***************************************************************************/
   public static getFieldDefaultValues(tableMetaData: QTableMetaData): { [fieldName: string]: any }
   {
      const defaultValues: { [fieldName: string]: any } = {};
      for (let fieldName of tableMetaData.fields.keys())
      {
         const defaultValue = tableMetaData.fields.get(fieldName).defaultValue;
         if (defaultValue !== undefined && defaultValue !== null)
         {
            defaultValues[fieldName] = defaultValue;
         }
      }
      return (defaultValues);
   }


   /***************************************************************************
    * Format any DATE_TIME values in `values` from the backend's raw ISO/UTC
    * string into the form-friendly local-zone string Formik fields render.
    * Mutates `values` in place so callers can chain this between adjuster
    * passes and setInitialValues/setFormikValues.
    ***************************************************************************/
   public static formatDateTimeValuesForForm(values: { [fieldName: string]: any }, tableMetaData: QTableMetaData): void
   {
      tableMetaData.fields.forEach((fieldMetaData, fieldName) =>
      {
         if (fieldMetaData.type === QFieldType.DATE_TIME && values[fieldName])
         {
            values[fieldName] = ValueUtils.formatDateTimeValueForForm(values[fieldName]);
         }
      });
   }


   /***************************************************************************
    * Build the values payload for create/update from raw Formik values.
    *
    * - JSON-round-trips to strip non-serializable extras (e.g. File → {}).
    * - DATE_TIME: drops fields whose form-formatted value matches initialValues
    *   (unchanged), otherwise converts the local-zone string to UTC for the
    *   backend.
    * - BLOB: drops string values (existing display value, not a new upload),
    *   otherwise re-assigns the original (non-serialized) File so it survives
    *   the JSON round-trip and qController posts it via multipart/form-data.
    *
    * Caller-supplied initialValues must already have DATE_TIME fields formatted
    * via ValueUtils.formatDateTimeValueForForm so the unchanged-comparison
    * works at the form-formatted level.
    ***************************************************************************/
   public static sanitizeFormValuesForSave(values: any, tableMetaData: QTableMetaData, initialValues: { [fieldName: string]: any }): any
   {
      const valuesToPost: { [fieldName: string]: any } = JSON.parse(JSON.stringify(values));

      tableMetaData.fields.forEach((fieldMetaData, fieldName) =>
      {
         if (fieldMetaData.type === QFieldType.DATE_TIME && valuesToPost[fieldName])
         {
            if (initialValues[fieldName] === valuesToPost[fieldName])
            {
               delete valuesToPost[fieldName];
            }
            else
            {
               valuesToPost[fieldName] = ValueUtils.frontendLocalZoneDateTimeStringToUTCStringForBackend(valuesToPost[fieldName]);
            }
         }

         if (fieldMetaData.type === QFieldType.BLOB)
         {
            if (typeof valuesToPost[fieldName] === "string")
            {
               delete valuesToPost[fieldName];
            }
            else
            {
               valuesToPost[fieldName] = values[fieldName];
            }
         }
      });

      return valuesToPost;
   }


   /***************************************************************************
    * check if a field has a specific behavior name on it.
    ***************************************************************************/
   private static hasBehavior(fieldMetaData: QFieldMetaData, behaviorName: string): boolean
   {
      if (fieldMetaData && fieldMetaData.behaviors)
      {
         for (let i = 0; i < fieldMetaData.behaviors.length; i++)
         {
            if (fieldMetaData.behaviors[i] == behaviorName)
            {
               return (true);
            }
         }
      }

      return (false);
   }

}

export default DynamicFormUtils;
